import { ReactNode, ComponentType, createContext, useContext } from 'react';

const EMPTY: unique symbol = Symbol();

/**
 * Props for a store provider component
 */
export interface StoreProviderProps<Value, State = void> {
    initialState?: State;
    value?: Value;
    children: ReactNode;
}

/**
 * Interface for a store created with createStore
 */
export interface Store<Value, State = void> {
    Provider: ComponentType<StoreProviderProps<Value, State>>;
    useStore: () => Value;
}

/**
 * Creates a store with a Provider component and a hook to access the store
 * @param useHook A hook that returns the store value
 */
export function createStore<Value, State = void>(
    useHook: (initialState?: State) => Value,
): Store<Value, State> {
    const hookName = useHook.name;
    const Context = createContext<Value | typeof EMPTY>(EMPTY);

    function Provider(props: StoreProviderProps<Value, State>) {
        const value = useHook(props.initialState);
        return <Context.Provider value={props.value ?? value}>{props.children}</Context.Provider>;
    }

    function useStore(): Value {
        const value = useContext(Context);
        if (value === EMPTY) {
            throw new Error(
                `Component must be wrapped with ${hookName}'s provider before using ${hookName}`,
            );
        }
        return value;
    }

    return { Provider, useStore };
}

/**
 * Hook to access a store created with createStore
 * @param store The store to access
 */
export function useStore<Value, State = void>(store: Store<Value, State>): Value {
    return store.useStore();
}
